/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Button,
    IconButton,
    Typography,
    Grid,
    Paper,
    Tooltip,
    Alert,
    Autocomplete
} from "@mui/material";
import { Add, Delete, Block } from "@mui/icons-material";
import StandardFormDialog from "../../components/StandardFormDialog";
import api from "../../services/api";
import { formatarCPF, formatarTelefone, formatarCEP, formatarCNPJ } from "../../utils/formatadores";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ptBR } from 'date-fns/locale';
import { parseISO, format, isValid } from 'date-fns';
import { useAuth } from "../../hooks/useAuth";

export default function CadastroBeneficiario({ open, onClose, beneficiarioId, onSuccess }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Configurações da Empresa
    const [configEmpresa, setConfigEmpresa] = useState({
        nu_max_dependentes: 4,
        bo_permite_substituicao: false,
        nu_meses_carencia: 12,
        nu_vigencia_contrato: 12
    });

    const [representantes, setRepresentantes] = useState([]);

    const [titular, setTitular] = useState({
        st_nome: "",
        dt_nascimento: "",
        en_sexo: "M",
        st_cpf: "",
        st_rg: "",
        en_estado_civil: "Solteiro(a)",
        st_telefone: "",
        st_email: "",
        st_cep: "",
        st_logradouro: "",
        st_numero: "",
        st_complemento: "",
        st_bairro: "",
        st_cidade: "",
        st_estado: "",
        dt_adesao: format(new Date(), 'yyyy-MM-dd'),
        dt_renovacao: "",
        id_representante: ""
    });

    const [dependentes, setDependentes] = useState([]);

    useEffect(() => {
        carregarConfiguracoes();
        carregarRepresentantes();
        if (beneficiarioId) {
            carregarBeneficiario();
        }
    }, [beneficiarioId, user]);

    const carregarConfiguracoes = async () => {
        try {
            const idEmpresa = user?.id_empresa || 1;
            const res = await api.get(`/empresa/${idEmpresa}`);
            if (res.data.status === 'sucesso') {
                const { nu_max_dependentes, bo_permite_substituicao, nu_meses_carencia, nu_vigencia_contrato } = res.data.dados;
                setConfigEmpresa({
                    nu_max_dependentes: nu_max_dependentes !== undefined ? nu_max_dependentes : 4,
                    bo_permite_substituicao: !!bo_permite_substituicao,
                    nu_meses_carencia: nu_meses_carencia !== undefined ? nu_meses_carencia : 12,
                    nu_vigencia_contrato: nu_vigencia_contrato !== undefined ? nu_vigencia_contrato : 12
                });
            }
        } catch (err) {
            console.error("Erro ao carregar configurações da empresa", err);
        }
    };

    const carregarRepresentantes = async () => {
        try {
            const res = await api.get('/representante');
            if (res.data.status === 'sucesso') {
                setRepresentantes(res.data.dados);
            }
        } catch (err) {
            console.error("Erro ao carregar representantes", err);
        }
    };

    const carregarBeneficiario = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/beneficiario/${beneficiarioId}`);
            if (res.data.status === 'sucesso') {
                const dados = res.data.dados;

                setTitular({
                    ...dados,
                    st_nome: dados.usuario?.st_nome || "",
                    st_email: dados.usuario?.st_email || "",
                    st_telefone: dados.usuario?.st_telefone || "",
                    id_representante: dados.id_representante || ""
                });

                const depsMapeados = (dados.dependentes || []).map(d => ({
                    ...d,
                    st_nome: d.usuario?.st_nome || "",
                    st_email: d.usuario?.st_email || "",
                    st_telefone: d.usuario?.st_telefone || "",
                    novo: false
                }));
                setDependentes(depsMapeados);
            }
        } catch (err) {
            console.error(err);
            setError("Erro ao carregar dados do beneficiário.");
        } finally {
            setLoading(false);
        }
    };

    const handleTitularChange = (e) => {
        const { name, value } = e.target;
        setTitular(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name, newValue) => {
        let dateStr = "";
        if (newValue && isValid(newValue)) {
            dateStr = format(newValue, 'yyyy-MM-dd');
        }

        setTitular(prev => {
            const newState = { ...prev, [name]: dateStr };

            // Recalcular renovação se adesão mudar
            if (name === 'dt_adesao' && dateStr && configEmpresa.nu_vigencia_contrato) {
                const adesao = new Date(dateStr);
                // Evitar mutação direta e problemas de timezone simples
                // Adicionar meses
                const renovacao = new Date(adesao.getFullYear(), adesao.getMonth() + configEmpresa.nu_vigencia_contrato, adesao.getDate());
                newState.dt_renovacao = format(renovacao, 'yyyy-MM-dd');
            }
            return newState;
        });
    };

    // Efeito para calcular renovação inicial se for novo cadastro
    useEffect(() => {
        if (!beneficiarioId && configEmpresa.nu_vigencia_contrato && titular.dt_adesao) {
            const adesao = new Date(titular.dt_adesao);
            const renovacao = new Date(adesao.getFullYear(), adesao.getMonth() + configEmpresa.nu_vigencia_contrato, adesao.getDate());
            setTitular(prev => ({ ...prev, dt_renovacao: format(renovacao, 'yyyy-MM-dd') }));
        }
    }, [configEmpresa.nu_vigencia_contrato, beneficiarioId]);

    const handleBuscaCEP = async () => {
        if (titular.st_cep && titular.st_cep.length >= 8) {
            try {
                const cepLimpo = titular.st_cep.replace(/\D/g, '');
                if (cepLimpo.length === 8) {
                    const res = await api.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
                    if (!res.data.erro) {
                        setTitular(prev => ({
                            ...prev,
                            st_logradouro: res.data.logradouro,
                            st_bairro: res.data.bairro,
                            st_cidade: res.data.localidade,
                            st_estado: res.data.uf
                        }));
                    }
                }
            } catch (err) {
                console.error("Erro ao buscar CEP", err);
            }
        }
    };

    const adicionarDependente = () => {
        if (dependentes.length >= configEmpresa.nu_max_dependentes) {
            setError(`Limite de ${configEmpresa.nu_max_dependentes} dependentes atingido.`);
            return;
        }
        setDependentes([...dependentes, {
            st_nome: "",
            dt_nascimento: "",
            en_sexo: "M",
            st_cpf: "",
            st_rg: "",
            en_estado_civil: "Solteiro(a)",
            st_telefone: "",
            st_email: "",
            novo: true
        }]);
    };

    const atualizarDependente = (index, field, value) => {
        const deps = [...dependentes];
        deps[index][field] = value;
        setDependentes(deps);
    };

    const removerDependenteNovo = (index) => {
        const deps = [...dependentes];
        if (deps[index].novo) {
            deps.splice(index, 1);
            setDependentes(deps);
        }
    };

    const inativarDependenteExistente = async (index) => {
        const dep = dependentes[index];
        const msg = configEmpresa.bo_permite_substituicao
            ? "Deseja realmente inativar este dependente? A vaga será liberada imediatamente."
            : `ATENÇÃO: De acordo com as regras, inativar este dependente NÃO liberará a vaga (Carência de ${configEmpresa.nu_meses_carencia} meses). O dependente ficará inativo mas ocupando o slot. Deseja continuar?`;

        if (window.confirm(msg)) {
            try {
                setLoading(true);
                await api.delete(`/beneficiario/dependente/${dep.id_beneficiario}`);
                await carregarBeneficiario();
                setSuccess("Dependente inativado com sucesso.");
            } catch (err) {
                setError(err.response?.data?.mensagem || "Erro ao inativar dependente.");
            } finally {
                setLoading(false);
            }
        }
    };

    const salvar = async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = {
                ...titular,
                dependentes: dependentes.map(d => ({
                    ...d,
                    id_beneficiario: d.id_beneficiario || undefined,
                    id: d.id_beneficiario || undefined
                }))
            };

            if (beneficiarioId) {
                await api.put(`/beneficiario/${beneficiarioId}`, payload);
                setSuccess("Beneficiário atualizado com sucesso!");
            } else {
                await api.post('/beneficiario', payload);
                setSuccess("Beneficiário criado com sucesso!");
            }

            setTimeout(() => {
                onSuccess("Operação realizada com sucesso");
            }, 1000);

        } catch (err) {
            setError(err.response?.data?.mensagem || err.message || "Erro ao salvar.");
            setLoading(false);
        }
    };

    const tabDados = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {beneficiarioId && (
                <Box>
                    <TextField fullWidth label="Número Sequencial de Cadastro" value={beneficiarioId} disabled />
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                    <DatePicker
                        label="Data de Adesão"
                        value={titular.dt_adesao ? parseISO(titular.dt_adesao) : null}
                        onChange={(newValue) => handleDateChange('dt_adesao', newValue)}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                    <DatePicker
                        label="Data para Renovação"
                        value={titular.dt_renovacao ? parseISO(titular.dt_renovacao) : null}
                        disabled
                        slotProps={{ textField: { fullWidth: true, helperText: `Vigência: ${configEmpresa.nu_vigencia_contrato} meses` } }}
                    />
                </Box>
            </Box>

            <Box>
                <TextField fullWidth label="Nome Completo" name="st_nome" value={titular.st_nome} onChange={handleTitularChange} required />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                    <DatePicker
                        label="Data de Nascimento"
                        value={titular.dt_nascimento ? parseISO(titular.dt_nascimento) : null}
                        onChange={(newValue) => handleDateChange('dt_nascimento', newValue)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                required: true,
                                error: false,
                                helperText: beneficiarioId ? "Não alterável" : ""
                            }
                        }}
                        disabled={!!beneficiarioId}
                    />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth label="CPF" name="st_cpf" value={formatarCPF(titular.st_cpf)} onChange={e => handleTitularChange({ target: { name: 'st_cpf', value: e.target.value.replace(/\D/g, '') } })} required disabled={!!beneficiarioId} />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth label="RG" name="st_rg" value={titular.st_rg} onChange={handleTitularChange} disabled={!!beneficiarioId} />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth select label="Sexo" name="en_sexo" value={titular.en_sexo} onChange={handleTitularChange} disabled={!!beneficiarioId}>
                        <MenuItem value="M">Masculino</MenuItem>
                        <MenuItem value="F">Feminino</MenuItem>
                        <MenuItem value="O">Outro</MenuItem>
                    </TextField>
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth select label="Estado Civil" name="en_estado_civil" value={titular.en_estado_civil} onChange={handleTitularChange}>
                        <MenuItem value="Solteiro(a)">Solteiro(a)</MenuItem>
                        <MenuItem value="Casado(a)">Casado(a)</MenuItem>
                        <MenuItem value="Divorciado(a)">Divorciado(a)</MenuItem>
                        <MenuItem value="Viuvo(a)">Viúvo(a)</MenuItem>
                    </TextField>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth label="Telefone" name="st_telefone" value={formatarTelefone(titular.st_telefone)} onChange={e => handleTitularChange({ target: { name: 'st_telefone', value: e.target.value.replace(/\D/g, '') } })} required />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth label="Email" name="st_email" type="email" value={titular.st_email} onChange={handleTitularChange} helperText="Login do usuário" />
                </Box>
            </Box>

            {/* Associação com Representante */}
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#f9f9f9' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mb: 2 }}>Associação com Representante</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={representantes}
                            getOptionLabel={(option) => `${option.st_nome} (${option.st_cpf_cnpj || 'Sem Doc'})`}
                            value={representantes.find(r => r.id_representante === titular.id_representante) || null}
                            onChange={(event, newValue) => {
                                handleTitularChange({ target: { name: 'id_representante', value: newValue ? newValue.id_representante : "" } });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Buscar Representante" placeholder="Digite o nome ou documento..." fullWidth />
                            )}
                            noOptionsText="Nenhum representante encontrado"
                        />
                    </Grid>
                    {titular.id_representante && (
                        (() => {
                            const rep = representantes.find(r => r.id_representante === titular.id_representante);
                            if (!rep) return null;
                            return (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 1, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#fff' }}>
                                        <Typography variant="body2" fontWeight="bold">{rep.st_nome}</Typography>
                                        <Typography variant="caption" display="block">Documento: {formatarCPF(rep.st_cpf_cnpj) || formatarCNPJ(rep.st_cpf_cnpj) || rep.st_cpf_cnpj}</Typography>
                                        <Typography variant="caption" display="block">Email: {rep.st_email}</Typography>
                                        <Typography variant="caption" display="block">Região: {rep.st_regiao || 'N/A'}</Typography>
                                    </Box>
                                </Grid>
                            );
                        })()
                    )}
                </Grid>
            </Paper>
        </Box>
    );

    const tabEndereco = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 150px' }}>
                    <TextField fullWidth label="CEP" name="st_cep" value={formatarCEP(titular.st_cep)} onChange={e => handleTitularChange({ target: { name: 'st_cep', value: e.target.value.replace(/\D/g, '') } })} onBlur={handleBuscaCEP} required />
                </Box>
                <Box sx={{ flex: '3 1 300px' }}>
                    <TextField fullWidth label="Logradouro" name="st_logradouro" value={titular.st_logradouro} onChange={handleTitularChange} required />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 100px' }}>
                    <TextField fullWidth label="Número" name="st_numero" value={titular.st_numero} onChange={handleTitularChange} required />
                </Box>
                <Box sx={{ flex: '2 1 200px' }}>
                    <TextField fullWidth label="Complemento" name="st_complemento" value={titular.st_complemento} onChange={handleTitularChange} />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth label="Bairro" name="st_bairro" value={titular.st_bairro} onChange={handleTitularChange} required />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                    <TextField fullWidth label="Cidade" name="st_cidade" value={titular.st_cidade} onChange={handleTitularChange} required />
                </Box>
                <Box sx={{ flex: '0 1 100px' }}>
                    <TextField fullWidth label="UF" name="st_estado" value={titular.st_estado} onChange={handleTitularChange} required />
                </Box>
            </Box>
        </Box>
    );

    const tabDependentes = (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                <Typography variant="h6">Dependentes ({dependentes.length}/{configEmpresa.nu_max_dependentes})</Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={adicionarDependente} disabled={dependentes.length >= configEmpresa.nu_max_dependentes}>
                    Adicionar
                </Button>
            </Box>

            {dependentes.length >= configEmpresa.nu_max_dependentes && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Limite de vagas atingido. {configEmpresa.bo_permite_substituicao ? 'Substituição permitida.' : 'Vagas ocupadas por inativos não podem ser reutilizadas durante a carência.'}
                </Alert>
            )}

            {dependentes.map((dep, index) => {
                const isInativo = dep.bo_excluido;
                const isSalvo = !dep.novo;

                return (
                    <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: isInativo ? '#f5f5f5' : 'background.default', border: isInativo ? '1px dashed #ccc' : '1px solid #eee', opacity: isInativo ? 0.7 : 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" color={isInativo ? "error" : "primary"}>
                                {dep.novo ? "Novo Dependente" : isInativo ? "Dependente Inativo (Vaga Ocupada)" : "Dependente Ativo"}
                            </Typography>
                            <Box>
                                {dep.novo && (
                                    <Tooltip title="Remover da lista">
                                        <IconButton size="small" onClick={() => removerDependenteNovo(index)} color="error"><Delete /></IconButton>
                                    </Tooltip>
                                )}
                                {isSalvo && !isInativo && (
                                    <Tooltip title="Inativar Dependente">
                                        <IconButton size="small" onClick={() => inativarDependenteExistente(index)} color="warning"><Block /></IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '2 1 300px' }}>
                                    <TextField fullWidth size="small" label="Nome" value={dep.st_nome} onChange={e => atualizarDependente(index, 'st_nome', e.target.value)} disabled={isInativo} required />
                                </Box>
                                <Box sx={{ flex: '1 1 150px' }}>
                                    <TextField fullWidth size="small" label="CPF" value={formatarCPF(dep.st_cpf)} onChange={e => atualizarDependente(index, 'st_cpf', e.target.value.replace(/\D/g, ''))} disabled={!dep.novo} />
                                </Box>
                                <Box sx={{ flex: '1 1 150px' }}>
                                    <TextField fullWidth size="small" label="RG" value={dep.st_rg} onChange={e => atualizarDependente(index, 'st_rg', e.target.value)} disabled={!dep.novo} />
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '1 1 150px' }}>
                                    <DatePicker
                                        label="Data de Nascimento"
                                        value={dep.dt_nascimento ? parseISO(dep.dt_nascimento) : null}
                                        onChange={(newValue) => {
                                            const dateStr = (newValue && isValid(newValue)) ? format(newValue, 'yyyy-MM-dd') : '';
                                            atualizarDependente(index, 'dt_nascimento', dateStr);
                                        }}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        disabled={!dep.novo}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 120px' }}>
                                    <TextField fullWidth size="small" select label="Sexo" value={dep.en_sexo} onChange={e => atualizarDependente(index, 'en_sexo', e.target.value)} disabled={!dep.novo}>
                                        <MenuItem value="M">Masculino</MenuItem>
                                        <MenuItem value="F">Feminino</MenuItem>
                                        <MenuItem value="O">Outro</MenuItem>
                                    </TextField>
                                </Box>
                                <Box sx={{ flex: '1 1 150px' }}>
                                    <TextField fullWidth size="small" select label="Estado Civil" value={dep.en_estado_civil} onChange={e => atualizarDependente(index, 'en_estado_civil', e.target.value)} disabled={isInativo}>
                                        <MenuItem value="Solteiro(a)">Solteiro(a)</MenuItem>
                                        <MenuItem value="Casado(a)">Casado(a)</MenuItem>
                                        <MenuItem value="Divorciado(a)">Divorciado(a)</MenuItem>
                                        <MenuItem value="Viuvo(a)">Viúvo(a)</MenuItem>
                                    </TextField>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '1 1 150px' }}>
                                    <TextField fullWidth size="small" label="Telefone" value={formatarTelefone(dep.st_telefone)} onChange={e => atualizarDependente(index, 'st_telefone', e.target.value.replace(/\D/g, ''))} disabled={isInativo} />
                                </Box>
                                <Box sx={{ flex: '2 1 250px' }}>
                                    <TextField fullWidth size="small" label="Email" value={dep.st_email} onChange={e => atualizarDependente(index, 'st_email', e.target.value)} disabled={isInativo} />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                );
            })}
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                *Regra de negócio: {configEmpresa.bo_permite_substituicao
                    ? "Substituição de dependentes permitida a qualquer momento."
                    : `Carência para substituição: ${configEmpresa.nu_meses_carencia} meses após criação ou inativação.`}
            </Typography>
        </Box>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <StandardFormDialog
                open={open}
                onClose={onClose}
                title={beneficiarioId ? "Editar Beneficiário" : "Novo Beneficiário"}
                tabs={[
                    { label: "Dados Pessoais", content: tabDados },
                    { label: "Endereço", content: tabEndereco },
                    { label: "Dependentes", content: tabDependentes }
                ]}
                onSave={salvar}
                loading={loading}
                error={error}
                success={success}
                maxWidth="md"
            />
        </LocalizationProvider>
    );
}
